import { getLocales } from 'expo-localization';
import { LinearGradient } from 'expo-linear-gradient';
import React, { useEffect, useRef, useState } from 'react';
import {
  Platform,
  Pressable,
  ScrollView,
  View,
  useColorScheme,
  Image,
  useWindowDimensions,
  Linking,
  StyleSheet,
  NativeSyntheticEvent,
  TextInputEndEditingEventData,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { AuthenticateClient, DeviceTheme, ExternalExistsRequest, ExternalLoginRequest, MissionsClient, RegistrationDto } from '../api/api.g';
import CustomButton from '../components/common/CustomButton';
import CustomTextInput, { InputTypeValidation } from '../components/common/CustomTextInput';
import Label, { LabelType } from '../components/common/Label';
import Loading from '../components/common/Loading';
import { useAppDispatch, useAppSelector } from '../hooks/store-hook';
import { authActions } from '../models/helpers/platformImportHelper';
import { userPrefsActions } from '../store/userPrefsSlice';
import useCustomTheme from '../hooks/theme-hook';
import { StylesHelper } from '../models/helpers/stylesHelper';
import useTranslation from '../hooks/translation-hook';
import { LoggedState } from '../models/AuthModelsCommon';
import { MISSION_REFERRAL_SIGN_UP_SOURCE } from '../models/SignUpSources';
import useMyAccountActions from '../hooks/myAccountActions-hook';
import { missionsActions } from '../store/missionsSlice';
import { drawerActions } from '../store/drawerSlice';
import { modalActions } from '../store/modalSlice';
import { StackActions, useFocusEffect, useIsFocused, useNavigation } from '@react-navigation/native';
import useApi from '../hooks/api-hook';
import { AnalyticsHelper } from '../helpers/analyticsHelper';
import { useMutation, useQuery } from '@tanstack/react-query';
import { StringHelper } from '../helpers/stringHelper';
import CustomMarkdown from '../components/common/CustomMarkDown';
import Logo from '../../assets/login-page-logo.svg';
import { getAuth, getMultiFactorResolver, onAuthStateChanged, PhoneAuthProvider, RecaptchaVerifier, PhoneMultiFactorGenerator } from 'firebase/auth';
import { getLoginFocusAction, LoginFocusAction } from '../helpers/loginFocusAction';

export enum LoginPageState {
  Login,
  Register,
  RegisterNewPassword,
  ForgotPassword,
  ShowTermsAndConditions,
  OldWeakPassword,
  twoFactorAuth,
}

const getFirstName = (name?: string) => {
  const trimmedName = name?.trim();

  if (!trimmedName) {
    return undefined;
  }

  return trimmedName.split(/\s+/)[0];
};

const Login = ({ route }) => {
  const auth = getAuth();
  const theme = useCustomTheme();
  const authStore = useAppSelector((selector) => selector.auth);
  const userPrefs = useAppSelector((selector) => selector.userPrefs);
  const t = useTranslation();
  const tLogin = useTranslation({ defaultPath: 'login' });
  const myAccountActions = useMyAccountActions();
  const navigation = useNavigation();
  const windowDimensions = useWindowDimensions();
  const insets = useSafeAreaInsets();

  const apiAuth = useApi(AuthenticateClient);
  const apiMissions = useApi(MissionsClient);
  const routeParams = route?.params;
  const referralEmail = typeof routeParams?.email === 'string' ? routeParams.email.trim() : '';
  const referralToken = typeof routeParams?.referralToken === 'string' ? routeParams.referralToken.trim() : '';
  const referralAvailabilityChecked = routeParams?.referralAvailabilityChecked === 'true';
  const isReferralLogin = route?.name === 'referralLogin' || !!routeParams?.missionId || !!referralToken;
  const isDesktopLoginLayout = windowDimensions.width >= 1366;
  const routeReferralLoginFirstNameParam =
    typeof routeParams?.referrerFirstName === 'string' && routeParams.referrerFirstName.trim() ? routeParams.referrerFirstName.trim() : undefined;
  const routeReferralLoginFirstName = routeReferralLoginFirstNameParam ?? t('mission-referral-popup-default-first-name');

  // const [passwordLogin, setPasswordLogin] = useState(false);
  // const [register, setRegister] = useState(false);
  const [pageState, setPageState] = useState(LoginPageState.Login);
  const [recaptchaVerifier, setRecaptchaVerifier] = useState(null);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loginComponent, setLoginComponent] = useState(<></>);
  const [returnButtonComponent, setReturnButtonComponent] = useState(<></>);
  const [loading, setLoading] = useState(true);
  const [goNextStep, setGoNextStep] = useState(false);
  const [referralLoginFirstName, setReferralLoginFirstName] = useState(routeReferralLoginFirstName);

  const [showSignUpButton, setShowSignUpButton] = useState(true);

  const [formTouched, setFormTouched] = useState(false);
  const [userMessage, setUserMessage] = useState<string | undefined>();

  const [emailLoginValidationError, setEmailLoginValidationError] = useState<string | undefined>(undefined);
  const [passwordLoginInputValidationError, setPasswordLoginInputValidationError] = useState<string | undefined>(undefined);
  const [passwordLoginValidationError, setPasswordLoginValidationError] = useState<string | undefined>(undefined);
  const [emailRegisterValidationError, setEmailRegisterValidationError] = useState<string | undefined>(undefined);
  const [passwordRegisterValidationError, setPasswordRegisterValidationError] = useState<string | undefined>(undefined);
  const [passwordRegisterConfValidationError, setPasswordRegisterConfValidationError] = useState<string | undefined>(undefined);

  const [formPasswordTouched, setFormPasswordTouched] = useState(false);
  const [formPasswordConfirmTouched, setFormPasswordConfirmTouched] = useState(false);

  const [signUpSource, setSignUpSource] = useState<string>();
  const [loginMethod, setLoginMethod] = useState<string>();
  const [goToSignUp, setGoToSignUp] = useState<boolean>();
  const [updateForm, setUpdateForm] = useState(false);
  const referralLoginSetupKeyRef = useRef<string | undefined>(undefined);
  const [phoneInfoOptions, setPhoneInfoOptions] = useState<any>({});
  const [phoneVerificationId, setPhoneVerificationId] = useState<string>();
  const [verificationCode, setVerificationCode] = useState('');
  const [verificationCodeError, setVerificationCodeError] = useState<string | undefined>(undefined);
  const [resolver, setResolver] = useState(null);
  const [timeLeft, setTimeLeft] = useState(0);
  const [timerActive, setTimerActive] = useState(false);
  const fixedAuthInputStyle = { backgroundColor: '#ffffff', borderColor: '#b6b6b6', color: '#1a1818', WebkitTextFillColor: '#1a1818' } as any;
  const fixedAuthPlaceholderColor = '#989898';
  const invalidCredentialsMessage = tLogin('invalid-credentials');
  const authFieldErrorTextStyle = {
    color: '#ffffff',
    backgroundColor: '#a61e49',
    fontWeight: '700',
    marginTop: -4,
    position: 'relative',
    top: -4,
    zIndex: -1,
    paddingTop: 12,
    paddingRight: 12,
    paddingBottom: 6,
    paddingLeft: 12,
    borderTopLeftRadius: 0,
    borderTopRightRadius: 0,
    borderBottomLeftRadius: 6,
    borderBottomRightRadius: 6,
    lineHeight: 18,
    width: '100%',
    overflow: 'hidden',
  } as any;

  const isFocused = useIsFocused();
  const previousIsFocused = useRef(isFocused);
  const isExitingAnonymousTrial = useRef(false);
  const dispatch = useAppDispatch();

  const getInputEndEditingText = (e: NativeSyntheticEvent<TextInputEndEditingEventData>) => e.nativeEvent.text ?? '';

  useEffect(() => {
    setRecaptchaVerifier(
      new RecaptchaVerifier(auth, '2fa-recaptcha-container', {
        size: 'invisible',
        callback: (response) => {},
      })
    );
  }, []);

  useEffect(() => {
    let interval = null;
    if (timerActive && timeLeft > 0) {
      interval = setInterval(() => {
        setTimeLeft((prevTime) => prevTime - 1);
      }, 1000);
    } else if (timeLeft === 0) {
      clearInterval(interval);
    }
    return () => clearInterval(interval);
  }, [timerActive, timeLeft]);

  useFocusEffect(
    React.useCallback(() => {
      setEmail('');
      setPassword('');
      setConfirmPassword('');
      setPageState(LoginPageState.Login);
      setResolver(undefined);
      setEmailLoginValidationError(undefined);
      setPasswordLoginInputValidationError(undefined);
      setPasswordLoginValidationError(undefined);
      setEmailRegisterValidationError(undefined);
      setPasswordRegisterValidationError(undefined);
      setPasswordRegisterConfValidationError(undefined);
      setVerificationCodeError(undefined);
      setFormTouched(false);
      setFormPasswordTouched(false);
      setFormPasswordConfirmTouched(false);
    }, [])
  );

  useFocusEffect(
    React.useCallback(() => {
      if (authStore.logged !== undefined && authStore.logged !== LoggedState.NotLogged) {

        setLoading(true);
        dispatch(modalActions.resetModal());
      }
    }, [authStore.logged])
  );

  useEffect(() => {
    const regainedFocus = isFocused && !previousIsFocused.current;
    previousIsFocused.current = isFocused;
    const focusAction = getLoginFocusAction({
      regainedFocus,
      isAnonymous: authStore.logged === LoggedState.Anonymous,
      isAuthenticated: authStore.logged === LoggedState.Logged,
    });

    if (focusAction === LoginFocusAction.none) {
      return;
    }

    if (focusAction === LoginFocusAction.exitAnonymousTrial) {
      // Returning from the trial map is an explicit return to Login. End the
      // anonymous Firebase session and keep this route mounted so the form can render.
      isExitingAnonymousTrial.current = true;
      setLoading(false);
      dispatch(modalActions.resetModal());
      dispatch(authActions.logout());
      return;
    }

    let returnPath = authStore.returnPath;
    if (!returnPath || returnPath.indexOf('login') > -1 || returnPath.indexOf('logout') > -1) {
      returnPath = 'map';
    }

    // Authenticated users should not be able to return to a stale Login screen.
    navigation.dispatch(StackActions.replace(returnPath, authStore.params));
  }, [authStore.logged, authStore.params, authStore.returnPath, dispatch, isFocused, navigation]);

  useEffect(() => {
    setReferralLoginFirstName(routeReferralLoginFirstName);
  }, [routeReferralLoginFirstName]);

  useEffect(() => {
    if (!isReferralLogin || !referralToken || referralAvailabilityChecked || routeReferralLoginFirstNameParam || authStore.logged !== LoggedState.NotLogged) {
      return;
    }

    let isCurrent = true;
    let hasRequestedReferral = false;

    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      const hasSignedInUser = (!!currentUser && !currentUser.isAnonymous) || (!!authStore.user && !authStore.user.isAnonymous);

      if (!isCurrent || hasSignedInUser || hasRequestedReferral) {
        return;
      }

      hasRequestedReferral = true;

      apiMissions
        .getMissionReferral(referralToken, apiMissions.tokenSource?.token)
        .then((referral) => {
          const firstName = getFirstName(referral?.senderMysteryShopperName);

          if (isCurrent && firstName) {
            setReferralLoginFirstName(firstName);
          }
        })
        .catch(() => {});
    });

    return () => {
      isCurrent = false;
      unsubscribe();
    };
  }, [apiMissions, auth, authStore.logged, authStore.user, isReferralLogin, referralAvailabilityChecked, referralToken, routeReferralLoginFirstNameParam]);

  useEffect(() => {
    if (isReferralLogin) {
      const referralLoginSetupKey = `${referralToken}|${referralEmail}`;

      if (referralLoginSetupKeyRef.current !== referralLoginSetupKey) {
        referralLoginSetupKeyRef.current = referralLoginSetupKey;
        if (referralToken) {
          setSignUpSource(MISSION_REFERRAL_SIGN_UP_SOURCE);
          dispatch(authActions.setSignUpSource(MISSION_REFERRAL_SIGN_UP_SOURCE));
          dispatch(authActions.setReturnPath('missionReferral'));
          dispatch(authActions.setParams({ token: referralToken }));
        }

        if (referralEmail) {
          setEmail(referralEmail);
          setUpdateForm(true);
        }
      }
    }

    if (route?.params?.reset) {
      setUserMessage(tLogin('password_redefined'));
    }

    if (route?.params?.newLogin) {
      setLoading(false);
      setUpdateForm(true);
    }

    if (route?.params?.signUpSource) {
      const signUpValue = route?.params?.signUpSource;
      setSignUpSource(signUpValue);
      setGoToSignUp(true);
      dispatch(authActions.setSignUpSource(signUpValue));
    }

    if (route?.params?.signUp) {
      setGoToSignUp(true);
    }

    if (route?.params?.accountDeleted) {
      setUserMessage(t('deleted-account-confirmation-message'));
    }

    if (route?.params?.failToRegister) {
      setUserMessage(t('fail-to-register-message'));
    }

    if (route?.params?.changeEmail) {
      setUserMessage(t('email-change-validation'));
    }

    if (route?.params?.emailChanged) {
      setUserMessage(t('email-change-confirmation'));
    }

    if (route?.params?.emailVerified) {
      setUserMessage(t('Email-verified'));
    }

    if (route?.params?.secondFactorRemoved) {
      setUserMessage(t('Two-step-verification-has-been-reverted'));
    }
  }, [route.params, isReferralLogin, referralEmail, referralToken]);

  useEffect(() => {
    if (goToSignUp) {
      handleSignUpClick();
    }
  }, [goToSignUp]);

  useEffect(() => {
    if (authStore.logged === LoggedState.NotLogged) {
      isExitingAnonymousTrial.current = false;

      // Hiding the side menu
      myAccountActions.setVisibility(false);

      if (navigation.getState().index === 0) {
        setDefaultLanguageAndTheme();
      } else {
        dispatch(
          userPrefsActions.setBasicInfo({
            firstName: undefined,
            lastName: undefined,
            uiLanguage: userPrefs.uiLanguage,
            email: '',
            theme: undefined,
            languages: userPrefs.languages,
          })
        );
      }
      setFormTouched(false);

      setLoading(false);

      setEmailLoginValidationError(undefined);
      setPasswordLoginValidationError(undefined);

      setEmailRegisterValidationError(undefined);
      setPasswordRegisterValidationError(undefined);
      setPasswordRegisterConfValidationError(undefined);

      if (
        pageState !== LoginPageState.OldWeakPassword &&
        pageState !== LoginPageState.RegisterNewPassword &&
        !route?.params?.failToRegister &&
        !route?.params?.changeEmail &&
        !route?.params?.emailChanged &&
        !route?.params?.emailVerified &&
        !route?.params?.secondFactorRemoved
      ) {
        setUserMessage(undefined);
      }

      // Login page is the same for all users
      // Restarting drawer state
      dispatch(drawerActions.resetState());

      // Restarting map screen
      dispatch(missionsActions.setResetMap(true));
    }

    if (!isExitingAnonymousTrial.current && (authStore.logged === LoggedState.Logged || authStore.logged === LoggedState.Anonymous)) {
      let rPath = authStore.returnPath;

      if (!rPath || rPath === '' || rPath.indexOf('login') > -1 || rPath.indexOf('logout') > -1) {
        rPath = 'map';
      }

      // if (authStore.user.isAnonymous) {
      //   console.log('Anonymous', authStore.user.isAnonymous);
      //   setDefaultLanguageAndTheme();
      // }

      console.log(`%cLogged in redirecting to ${rPath}...`, 'color: #ffa600;');

      if (pageState === LoginPageState.Register || pageState === LoginPageState.RegisterNewPassword || pageState === LoginPageState.OldWeakPassword) {
        AnalyticsHelper.logSignUp(loginMethod ?? 'password');
        navigation.navigate({ name: rPath, params: authStore.params } as never);
      } else {
        navigation.navigate({ name: rPath, params: authStore.params } as never);
      }
    }
  }, [authStore.logged, pageState]);

  useEffect(() => {
    setUpdateForm(false);

    const backButton = (
      <CustomButton style={{ marginTop: 24 }} title={t('back')} textStyle={{ color: theme.primary }} secondary={true} onPress={handleReturn} />
    );

    switch (pageState) {
      case LoginPageState.twoFactorAuth:
        setLoginComponent(
          <View key="auth-screen-2fa">
            {phoneInfoOptions?.multiFactorHint && (
              <View style={{ marginBottom: 24 }}>
                <Label style={{ marginTop: 12, fontSize: 18, color: 'white', fontWeight: '400', width: '100%', flexShrink: 0 }} numberOfLines={10}>
                  {t('We-have-sent-a-verification-code-to')}
                </Label>
                <Label style={{ marginTop: 12, fontSize: 18, color: 'white', fontWeight: '600', width: '100%', flexShrink: 0 }} numberOfLines={10}>
                  {`${phoneInfoOptions.multiFactorHint.phoneNumber}`}
                </Label>
                <CustomTextInput
                  style={{ ...fixedAuthInputStyle, marginTop: 24 }}
                  forcePlaceholderTextColor={fixedAuthPlaceholderColor}
                  placeholder={t('Verification-Code')}
                  showErrorMessage
                  hasError={!!verificationCodeError}
                  errorMessage={verificationCodeError}
                  errorTextStyle={authFieldErrorTextStyle}
                  onChangeText={(value) => {
                    setVerificationCode(value);
                    setVerificationCodeError(undefined);
                  }}
                  value={verificationCode}
                  returnKeyType="done"
                  onSubmitEditing={() => {
                    if (verificationCode.length == 6) {
                      handleContinue();
                    }
                  }}
                />

                {resolver && (
                  <Pressable
                    disabled={timeLeft > 0}
                    style={{ marginTop: verificationCodeError ? 8 : 16 }}
                    onPress={() => {
                      sendCode(resolver);
                    }}
                  >
                    <Label style={{ color: 'white', fontWeight: '500', textDecorationLine: timeLeft > 0 ? 'none' : 'underline' }}>
                      {t('Resend-Code')}
                      {timeLeft ? ': ' + timeLeft + 's' : ''}
                    </Label>
                  </Pressable>
                )}
              </View>
            )}
          </View>
        );

        setReturnButtonComponent(backButton);
        break;
      case LoginPageState.Login:
        setLoginComponent(
          <View key="auth-screen-login">
            <View style={{ marginBottom: 24 }}>
              {isReferralLogin ? (
                <View style={{ marginBottom: isDesktopLoginLayout ? 24 : 40, alignItems: isDesktopLoginLayout ? 'flex-start' : 'center' }}>
                  <Label
                    numberOfLines={2}
                    style={{ ...StylesHelper.text(isDesktopLoginLayout ? 34 : 30, theme.primary, 600), textAlign: isDesktopLoginLayout ? 'left' : 'center' }}
                  >
                    {t('welcome')}
                  </Label>
                  <Label
                    numberOfLines={2}
                    style={{
                      ...StylesHelper.text(isDesktopLoginLayout ? 34 : 30, 'white', 600),
                      marginTop: -6,
                      textAlign: isDesktopLoginLayout ? 'left' : 'center',
                    }}
                  >
                    {t('mystery-shopper').toLowerCase()}
                  </Label>
                  <Label
                    numberOfLines={4}
                    style={{ marginTop: 28, color: 'white', fontSize: 16, lineHeight: 20, textAlign: isDesktopLoginLayout ? 'left' : 'center' }}
                  >
                    {t('referral-login-referred-body', { firstName: referralLoginFirstName })}
                  </Label>
                  <Label
                    numberOfLines={3}
                    style={{
                      marginTop: 10,
                      color: theme.primary,
                      fontSize: 16,
                      lineHeight: 20,
                      fontWeight: '600',
                      textAlign: isDesktopLoginLayout ? 'left' : 'center',
                    }}
                  >
                    {t('referral-login-connect-details')}
                  </Label>
                </View>
              ) : (
                <>
                  {windowDimensions.width >= 1366 && (
                    <View style={{ marginBottom: 30 }}>
                      <Label style={{ ...StylesHelper.text(40, theme.primary, 600) }}>{t('welcome')}</Label>
                      <Label style={{ ...StylesHelper.text(40, 'white', 600), marginTop: -10 }}>{t('mystery-shopper').toLowerCase()}</Label>
                    </View>
                  )}
                  {windowDimensions.width < 1366 && (
                    <View style={{ marginBottom: 60, alignContent: 'center' }}>
                      <Label style={{ ...StylesHelper.text(30, theme.primary, 600), textAlign: 'center' }}>{t('welcome')}</Label>
                      <Label style={{ ...StylesHelper.text(30, 'white', 600), marginTop: -10, textAlign: 'center' }}>
                        {t('mystery-shopper').toLowerCase()}
                      </Label>
                    </View>
                  )}
                </>
              )}
              <CustomTextInput
                style={fixedAuthInputStyle}
                forcePlaceholderTextColor={fixedAuthPlaceholderColor}
                required
                showErrorMessage
                hasError={!!emailLoginValidationError}
                errorMessage={emailLoginValidationError}
                errorTextStyle={authFieldErrorTextStyle}
                inputTypeValidation={InputTypeValidation.email}
                onValidationChange={(v) => (v.valid ? setEmailLoginValidationError(undefined) : setEmailLoginValidationError(v.message))}
                placeholder={t('email')}
                requiredMessage={tLogin('email_required')}
                key={2}
                value={email}
                onChangeText={(text) => {
                  setEmail(text);
                  setEmailLoginValidationError(undefined);
                  if ((text?.length ?? 0) > 0) {
                    setFormTouched(true);
                  }
                }}
                onKeyPress={(e) => {
                  {
                    if ((e as any).code === 'Enter') {
                      setGoNextStep(true);
                    }
                  }
                }}
              />
            </View>
            <CustomTextInput
              style={fixedAuthInputStyle}
              forcePlaceholderTextColor={fixedAuthPlaceholderColor}
              required
              showErrorMessage
              hasError={!!(passwordLoginValidationError || passwordLoginInputValidationError)}
              errorMessage={passwordLoginValidationError ? passwordLoginValidationError : passwordLoginInputValidationError}
              errorTextStyle={authFieldErrorTextStyle}
              requiredMessage={tLogin('password_required')}
              onValidationChange={(v) => (v.valid ? setPasswordLoginInputValidationError(undefined) : setPasswordLoginInputValidationError(v.message))}
              secureTextEntry={true}
              placeholder={t('password')}
              key={1}
              value={password}
              onChangeText={(text) => {
                if ((text?.length ?? 0) > 0) {
                  setFormPasswordTouched(true);
                }
                setFormTouched(true);
                setPassword(text);
                setPasswordLoginValidationError(undefined);
                setPasswordLoginInputValidationError(undefined);
              }}
              onEndEditing={(e) => {
                if (getInputEndEditingText(e).length > 0) {
                  setFormPasswordTouched(true);
                }
              }}
              onKeyPress={(e) => {
                if ((e as any).code === 'Enter') {
                  setGoNextStep(true);
                }
              }}
            />
            <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
              <Pressable onPress={handleSignUpClick} style={{ marginBottom: 24, marginTop: 8 }}>
                <Label style={{ fontSize: 16, color: 'white', textDecorationLine: 'underline', textAlign: 'right' }}>{tLogin('sign_up')}</Label>
              </Pressable>
              <Pressable onPress={startResetPassword} style={{ marginBottom: 24, marginTop: 8 }}>
                <Label style={{ fontSize: 16, color: 'white', textDecorationLine: 'underline', textAlign: 'right' }}>{tLogin('forgot_password')}</Label>
              </Pressable>
            </View>
          </View>
        );

        setReturnButtonComponent(<></>);

        break;

      case LoginPageState.OldWeakPassword:
      case LoginPageState.Register:
      case LoginPageState.RegisterNewPassword:
        setLoginComponent(
          <View key={`auth-screen-${pageState}`}>
            <Label
              style={{ fontSize: 20, color: 'white', fontWeight: 'normal', textTransform: 'uppercase', textAlign: 'center', marginBottom: 24 }}
              variant={LabelType.titleDark}
            >
              {pageState === LoginPageState.Register ? t('create_your_account') : t('create_your_password')}
            </Label>
            <View style={{ marginBottom: 24 }}>
              <Label style={{ width: '100%', textAlign: 'center', textTransform: 'uppercase', marginBottom: 8 }} variant={LabelType.labelWhite}>
                {pageState === LoginPageState.OldWeakPassword ? t('email') : t('enter_your_email')}
              </Label>
              <CustomTextInput
                style={fixedAuthInputStyle}
                forcePlaceholderTextColor={fixedAuthPlaceholderColor}
                required
                editable={pageState === LoginPageState.OldWeakPassword ? false : true}
                autoComplete={pageState === LoginPageState.OldWeakPassword ? 'off' : undefined}
                showErrorMessage
                hasError={!!emailRegisterValidationError}
                errorMessage={emailRegisterValidationError}
                errorTextStyle={authFieldErrorTextStyle}
                inputTypeValidation={InputTypeValidation.email}
                onValidationChange={(v) => (v.valid ? setEmailRegisterValidationError(undefined) : setEmailRegisterValidationError(v.message))}
                placeholder={t('email')}
                requiredMessage={tLogin('email_required')}
                key={3}
                value={email}
                onChangeText={(text) => {
                  if ((text?.length ?? 0) > 0) {
                    setFormTouched(true);
                  }
                  setEmail(text);
                  setEmailRegisterValidationError(undefined);
                }}
                onEndEditing={(e) => {
                  if (getInputEndEditingText(e).length === 0) {
                    setEmailRegisterValidationError(tLogin('email_required'));
                  }
                }}
              />
            </View>
            <View style={{ marginBottom: 24 }}>
              <Label style={{ width: '100%', textAlign: 'center', textTransform: 'uppercase', marginBottom: 8 }} variant={LabelType.labelWhite}>
                {t('create_your_password')}
              </Label>
              <CustomTextInput
                style={fixedAuthInputStyle}
                forcePlaceholderTextColor={fixedAuthPlaceholderColor}
                placeholder={t('password')}
                required
                showErrorMessage
                hasError={!!passwordRegisterValidationError}
                errorMessage={passwordRegisterValidationError}
                errorTextStyle={authFieldErrorTextStyle}
                requiredMessage={tLogin('password_required')}
                onValidationChange={(v) => (v.valid ? setPasswordRegisterValidationError(undefined) : setPasswordRegisterValidationError(v.message))}
                secureTextEntry={true}
                key={4}
                value={password}
                onChangeText={(text) => {
                  if ((text?.length ?? 0) > 0) {
                    setFormPasswordTouched(true);
                  }

                  setFormTouched(true);
                  setPassword(text);
                  setPasswordRegisterValidationError(undefined);
                }}
                onEndEditing={(e) => {
                  if (getInputEndEditingText(e).length > 0) {
                    setFormPasswordTouched(true);
                  }
                }}
                onKeyPress={(e) => {
                  if ((e as any).code === 'Enter') {
                    setGoNextStep(true);
                  }
                }}
              />
            </View>
            <View style={{ marginBottom: 24 }}>
              <Label style={{ width: '100%', textAlign: 'center', textTransform: 'uppercase', marginBottom: 8 }} variant={LabelType.labelWhite}>
                {t('confirm_your_password')}
              </Label>
              <CustomTextInput
                placeholder={t('password')}
                style={fixedAuthInputStyle}
                forcePlaceholderTextColor={fixedAuthPlaceholderColor}
                required
                showErrorMessage
                hasError={!!passwordRegisterConfValidationError}
                errorMessage={passwordRegisterConfValidationError}
                errorTextStyle={authFieldErrorTextStyle}
                requiredMessage={t('must_confirm_password')}
                secureTextEntry={true}
                key={5}
                value={confirmPassword}
                onChangeText={(text) => {
                  setFormPasswordConfirmTouched(false);
                  if ((text?.length ?? 0) > 0) {
                    setFormTouched(true);
                  }
                  setConfirmPassword(text);
                  setPasswordRegisterConfValidationError(undefined);
                }}
                // onChangeText={(text) => {
                //   setFormPasswordConfirmTouched(false);
                //   if ((text?.length ?? 0) > 0) {
                //     setFormTouched(true);
                //   }
                //   setConfirmPassword(text);
                // }}
                onEndEditing={(e) => {
                  if (getInputEndEditingText(e).length > 0) {
                    setFormPasswordConfirmTouched(true);
                  }
                }}
              />
            </View>
          </View>
        );

        setReturnButtonComponent(backButton);

        break;

      case LoginPageState.ForgotPassword:
        setLoginComponent(
          <View key="auth-screen-forgot-password">
            <View style={{ marginBottom: 24 }}>
              {windowDimensions.width >= 1366 && (
                <View style={{ marginBottom: 30 }}>
                  <Label style={{ ...StylesHelper.text(40, theme.primary, 600) }}>{tLogin('reset_password')}</Label>
                </View>
              )}
              {windowDimensions.width < 1366 && (
                <View style={{ marginBottom: 60, alignContent: 'center' }}>
                  <Label style={{ ...StylesHelper.text(30, theme.primary, 600), textAlign: 'center' }}>{tLogin('reset_password')}</Label>
                </View>
              )}
              <Label variant={LabelType.labelWhite} style={{ marginBottom: 16, textAlign: 'center' }}>
                {tLogin('email_reset_instructions')}
              </Label>
              <CustomTextInput
                style={fixedAuthInputStyle}
                forcePlaceholderTextColor={fixedAuthPlaceholderColor}
                required
                showErrorMessage
                hasError={!!emailLoginValidationError}
                errorMessage={emailLoginValidationError}
                errorTextStyle={authFieldErrorTextStyle}
                inputTypeValidation={InputTypeValidation.email}
                onValidationChange={(v) => (v.valid ? setEmailLoginValidationError(undefined) : setEmailLoginValidationError(v.message))}
                placeholder={t('email')}
                requiredMessage={tLogin('email_required')}
                key={2}
                value={email}
                onChangeText={(text) => {
                  setEmail(text);
                  setEmailLoginValidationError(undefined);
                  if ((text?.length ?? 0) > 0) {
                    setFormTouched(true);
                  }
                }}
                // onChangeText={(text) => {
                //   setEmail(text);
                //   if ((text?.length ?? 0) > 0) {
                //     setFormTouched(true);
                //   }
                // }}
                onKeyPress={(e) => {
                  {
                    if ((e as any).code === 'Enter') {
                      setGoNextStep(true);
                    }
                  }
                }}
              />
            </View>
          </View>
        );

        setReturnButtonComponent(backButton);
        break;
    }
  }, [
    pageState,
    isReferralLogin,
    isDesktopLoginLayout,
    referralLoginFirstName,
    email,
    emailLoginValidationError,
    passwordLoginValidationError,
    emailRegisterValidationError,
    passwordRegisterValidationError,
    passwordRegisterConfValidationError,
    userPrefs.theme,
    userPrefs.uiLanguage,
    t('email'),
    updateForm,
    verificationCodeError,
    timeLeft,
    resolver,
  ]);

  useEffect(() => {
    if (goNextStep) {
      setGoNextStep(false);
      handleContinue();
    }
  }, [goNextStep]);

  useEffect(() => {
    if (formPasswordTouched && formPasswordConfirmTouched && confirmPassword !== password) {
      setPasswordRegisterConfValidationError(t('passwords_must_match_error'));
    } else {
      setPasswordRegisterConfValidationError(undefined);
    }
  }, [confirmPassword, formPasswordConfirmTouched, formPasswordTouched]);

  useEffect(() => {
    if (email === '') {
      setUpdateForm(true);
    }
  }, [email]);

  useEffect(() => {
    setEmailLoginValidationError(undefined);
    setPasswordLoginInputValidationError(undefined);
    setPasswordLoginValidationError(undefined);
    setEmailRegisterValidationError(undefined);
    setPasswordRegisterValidationError(undefined);
    setPasswordRegisterConfValidationError(undefined);
    setVerificationCodeError(undefined);
    setFormTouched(false);
    setFormPasswordTouched(false);
    setFormPasswordConfirmTouched(false);
  }, [pageState]);

  const formIsValid = (): boolean => {
    return (
      !emailLoginValidationError &&
      !passwordLoginValidationError &&
      !emailRegisterValidationError &&
      !passwordRegisterValidationError &&
      !passwordRegisterConfValidationError
    );
  };

  const confirmAutoLogin = (user) => {
    if (user.code == 'auth/weak-password') {
      setUserMessage(tLogin('weak-old-password'));
      setPassword('');
      setPageState(LoginPageState.OldWeakPassword);
      return;
    }

    // First login on the phone - need top be different because the way that Firebase works for native
    if (Platform.OS !== 'web' && authStore.user) {
      authStore.user.getIdTokenResult().then((token) => {
        dispatch(authActions.setLogged({ idToken: token.token, user }));
      });
    }
  };

  const confirmAutoSignUpAndReset = (user) => {
    setEmail(user.email);
    resetPassword();
  };

  const sendCode = (resolver) => {
    let phoneInfoOptions = {
      multiFactorHint: resolver.hints[0],
      session: resolver.session,
    };
    setPhoneInfoOptions(phoneInfoOptions);
    const phoneAuthProvider = new PhoneAuthProvider(auth);
    phoneAuthProvider
      .verifyPhoneNumber(phoneInfoOptions, recaptchaVerifier)
      .then(function (verificationId) {
        setTimeLeft(180);
        setTimerActive(true);
        setPhoneVerificationId(verificationId);
      })
      .catch((e) => {
        setVerificationCodeError(t('Unable-to-send-the-verification-code-Please-try-ag'));
      });
  };

  const gotLoginInfo = (user) => {
    if (user.code == 'auth/multi-factor-auth-required') {
      if (resolver == undefined) {
        let resolver = getMultiFactorResolver(auth, user);
        setResolver(resolver);
        sendCode(resolver);
      }
      setPageState(LoginPageState.twoFactorAuth);
    }

    // If the password is wrong
    if (user.code == 'auth/wrong-password') {
      // setPasswordLoginValidationError(tLogin('wrong_password'));
      // setPasswordRegisterValidationError(tLogin('wrong_password'));
      setUserMessage(tLogin('invalid-credentials'));
      setLoading(false);
      return;
    }

    if (user.code === 'auth/too-many-requests') {
      setUserMessage(tLogin('too-many-request-login'));
      setLoading(false);
      return;
    }

    if (user.code == 'auth/user-not-found') {
      const requestData = new ExternalLoginRequest();
      requestData.email = email;
      requestData.password = password;

      apiAuth
        .externalLogin(requestData)
        .then((result) => {
          // Not able to login with old credentials
          if (!result) {
            const requestData = new ExternalExistsRequest();
            requestData.email = email;
            // Try to see that the account exists and it's wrong password
            apiAuth.externalExists(requestData).then((result) => {
              if (!result) {
                // The account doesn't exists on the old DB
                // setEmailLoginValidationError(tLogin('email_not_found'));
                // setEmailRegisterValidationError(tLogin('email_not_found'));
                setUserMessage(tLogin('invalid-credentials'));
                setLoading(false);
                return;
              } else {
                // In case the account exists but the password is wrong, create an account on Firebase and reset a random password
                user = undefined;
                setEmailLoginValidationError(undefined);
                setEmailRegisterValidationError(undefined);

                dispatch(authActions.register({ email: email.trim(), password: StringHelper.generateRandomString(8), callback: confirmAutoSignUpAndReset }));
                return;
              }
            });
          } else {
            // In this case automatically signup user
            user = undefined;
            setEmailLoginValidationError(undefined);
            setEmailRegisterValidationError(undefined);

            dispatch(authActions.register({ email: email.trim(), password, callback: confirmAutoLogin }));
            return;
          }
        })
        .catch(() => {
          setUserMessage(tLogin('invalid-credentials'));
          setLoading(false);
        });

      return;
    }

    if (user.code == 'auth/weak-password') {
      setPasswordRegisterValidationError(tLogin('weak_password'));
      setLoading(false);
      return;
    }

    if (user.code == 'auth/email-already-in-use') {
      setEmailRegisterValidationError(tLogin('email_in_use'));
      setLoading(false);
      return;
    }

    if (user.code) {
      setPasswordLoginValidationError(user.message);
      setPasswordRegisterValidationError(user.message);
      setLoading(false);
      return;
    }

    // First login on the phone - need top be different because the way that Firebase works for native
    if (Platform.OS !== 'web' && authStore.user) {
      authStore.user.getIdTokenResult().then((token) => {
        dispatch(authActions.setLogged({ idToken: token.token, user }));
      });
    }
  };

  useEffect(() => {
    if (pageState === LoginPageState.ForgotPassword && (!email || email.trimEnd() === '')) {
      setUpdateForm(true);
    }
  }, [email]);

  useEffect(() => {
    if (pageState === LoginPageState.ForgotPassword) {
      setEmail('');
      setPassword('');
    }
  }, [pageState]);

  const gotLoginMethod = (methods) => {
    // If the login popupo was closed
    if (methods.code === 'auth/popup-closed-by-user') {
      setLoading(false);
    }

    if ((methods?.length ?? 0) === 0) {
      setPageState(LoginPageState.Register);
    } else {
      setLoginMethod(methods[0]);
      switch (methods[0]) {
        case 'password':
          // setPasswordLogin(true);
          setPageState(LoginPageState.Login);
          setResolver(undefined);
          break;
        // case 'google.com':
        //   dispatch(authActions.loginWithGoogle({ callback: gotLoginInfo }));
        //   break;
      }
    }
  };

  const loginAnonymously = () => {
    setUserMessage(undefined);
    setLoading(true);
    dispatch(authActions.anonymousLogin({ callback: gotLoginInfo }));
  };

  const startResetPassword = () => {
    setPageState(LoginPageState.ForgotPassword);
  };

  const resetPassword = () => {
    setLoading(true);

    dispatch(
      authActions.sendPasswordResetEmail({
        email,
        lang: userPrefs.uiLanguage,
        callback: (e) => {
          if (e?.code) {
            let message = e?.message ?? tLogin('generic_error');

            if (e.code === 'auth/missing-email') {
              message = tLogin('email_required');
            }

            if (e.code === 'auth/user-not-found') {
              const requestData = new ExternalExistsRequest();
              requestData.email = email;

              // Try to see whether the account exists in the old DB.
              apiAuth.externalExists(requestData).then((result) => {
                if (!result) {
                  setEmail('');
                  setUserMessage(t('forgot_password_email_not_found_message', { email }));
                  setLoading(false);
                } else {
                  setEmailLoginValidationError(undefined);
                  setEmailRegisterValidationError(undefined);

                  setLoading(false);
                  setEmail(email);
                  setUserMessage(tLogin('create_new_password'));
                  setPageState(LoginPageState.RegisterNewPassword);
                }
              }).catch(() => {
                setEmail('');
                setUserMessage(t('forgot_password_email_not_found_message', { email }));
                setLoading(false);
              });

              return;
            }

            if (e.code === 'auth/invalid-email') {
              message = tLogin('email_required');
            }

            if (e.code === 'auth/too-many-requests') {
              message = tLogin('email_required');
            }

            setLoading(false);
            setUserMessage(message);
          } else {
            setLoading(false);
            setEmail('');
            setUserMessage(tLogin('forgot_password_message', { email }));
          }
        },
      })
    );
  };

  const handleContinue = () => {
    if (!formTouched || !formIsValid()) {
      if (!email) {
        setEmailLoginValidationError(tLogin('email_required'));
        return;
      }
    }

    if (pageState !== LoginPageState.ForgotPassword && (!password || password.trim() === '')) {
      setPasswordLoginValidationError(tLogin('password_required'));
      setPasswordRegisterValidationError(tLogin('password_required'));
      return;
    }

    if (
      (pageState !== LoginPageState.ForgotPassword && email && password && password.trim() !== '') ||
      (pageState === LoginPageState.twoFactorAuth && verificationCode.length == 6)
    ) {
      switch (pageState) {
        case LoginPageState.twoFactorAuth:
          setVerificationCodeError(undefined);
          const cred = PhoneAuthProvider.credential(phoneVerificationId, verificationCode);
          const multiFactorAssertion = PhoneMultiFactorGenerator.assertion(cred);
          resolver
            .resolveSignIn(multiFactorAssertion)
            .then(function (userCredential) {
              setPhoneVerificationId(undefined);
              gotLoginInfo(userCredential);
            })
            .catch(function (error) {
              if (error.code == 'auth/invalid-verification-code') {
                setVerificationCodeError(t('Invalid-verification-code'));
              } else {
                setVerificationCodeError(t('Something-went-wrong-please-try-again-later'));
              }
            });
          break;
        case LoginPageState.Login:
          setLoading(true);
          dispatch(authActions.loginPassword({ email: email.trim(), password, callback: gotLoginInfo }));
          break;
        case LoginPageState.OldWeakPassword:
        case LoginPageState.Register:
        case LoginPageState.RegisterNewPassword:
          if (confirmPassword !== password) {
            setPasswordRegisterConfValidationError(t('passwords_must_match_error'));
            return;
          } else {
            setPasswordRegisterConfValidationError(undefined);
          }

          dispatch(authActions.register({ email: email.trim(), password, callback: gotLoginInfo }));
          break;
      }
    } else {
      if (email) {
        switch (pageState) {
          case LoginPageState.ForgotPassword:
            resetPassword();
            break;
          default:
            dispatch(authActions.fetchSignInMethods({ email: email.trim(), callback: gotLoginMethod }));
            break;
        }
      } else {
        setEmailLoginValidationError(tLogin('email_required'));
        return;
      }
    }

    if (pageState !== LoginPageState.OldWeakPassword && pageState !== LoginPageState.RegisterNewPassword) {
      setUserMessage(undefined);
    }

    setShowSignUpButton(false);
  };

  const handleSignUpClick = () => {
    setEmail('');
    setPassword('');
    setConfirmPassword('');

    gotLoginMethod([]);

    setUserMessage(undefined);
    setShowSignUpButton(false);
    setPageState(LoginPageState.ShowTermsAndConditions);
  };

  const handleReturn = () => {
    // if (pageState === LoginPageState.Register) {
    setEmail(undefined);
    setPassword('');
    setConfirmPassword('');
    // }

    setShowSignUpButton(true);
    setUserMessage(undefined);
    setGoToSignUp(false);

    setPageState(LoginPageState.Login);
    setResolver(undefined);
  };

  const getLocale = () => {
    const locales = getLocales();
    return locales[0].languageCode;
  };

  const setDefaultLanguageAndTheme = () => {
    const language = Platform.OS === 'web' ? navigator.language : getLocale();

    if (!userPrefs.uiLanguage) {
      const newLanguages = [language.indexOf('-') === -1 ? language : language.substring(0, language.indexOf('-'))];
      dispatch(userPrefsActions.setLanguages(newLanguages));
      dispatch(userPrefsActions.setUiLanguage(newLanguages[0]));
    }

    if (!isReferralLogin) {
      dispatch(authActions.setReturnPath('map'));
    }
  };

  const getDesktopInfo = (title: string, body: string) => {
    return (
      <View style={{ maxWidth: '50%', paddingRight: 32 }}>
        <Label numberOfLines={3} style={{ color: theme.primary, fontSize: 16, marginRight: 8, fontWeight: '600' }}>
          {title}
        </Label>
        <Label numberOfLines={3} style={{ color: 'white', fontSize: 16, fontWeight: '300', marginBottom: 16 }}>
          {body}
        </Label>
      </View>
    );
  };

  const getOtherLanguageButton = () => {
    switch (userPrefs.uiLanguage) {
      case 'en':
        return (
          <Pressable
            onPress={() => {
              dispatch(userPrefsActions.setUiLanguageAndLanguages('fr'));
            }}
          >
            <Label variant={LabelType.titleWhiteSmall}>FR</Label>
          </Pressable>
        );
      default:
        return (
          <Pressable
            onPress={() => {
              dispatch(userPrefsActions.setUiLanguageAndLanguages('en'));
            }}
          >
            <Label variant={LabelType.titleWhiteSmall}>EN</Label>
          </Pressable>
        );
    }
  };

  const renderLoginLogo = (width = 85, height = 40, style?: any) => {
    if (Platform.OS !== 'web') {
      return (
        <View style={style}>
          <Logo width={width} height={height} />
        </View>
      );
    }

    return <Image style={[{ width, height }, style]} source={require('../../assets/login-page-logo.svg')} />;
  };

  const renderUserMessage = () =>
    userMessage && (userMessage?.length ?? 0) > 0 ? (
      <Label
        numberOfLines={5}
        variant={LabelType.titleSmall}
        style={{
          padding: 16,
          marginBottom: 24,
          backgroundColor: userMessage === invalidCredentialsMessage ? (theme.colorErrorPrimary?.toString() ?? '#a61e49') : theme.primary,
          color: 'white',
          ...StylesHelper.borderRadius(8),
        }}
      >
        {userMessage}
      </Label>
    ) : null;

  const renderAuthControls = (buttonStyle?: any) => (
    <View>
      {loginComponent}
      <CustomButton title={t('continue')} onPress={handleContinue} style={buttonStyle} />
      {returnButtonComponent}
    </View>
  );

  const renderTryAppLink = (textAlign: any) => {
    if (pageState !== LoginPageState.Login || (!isReferralLogin && signUpSource !== '' && signUpSource !== undefined)) {
      return null;
    }

    return (
      <View>
        <Pressable style={{ marginTop: 24 }} onPress={loginAnonymously}>
          <Label
            style={{
              fontSize: 14,
              color: 'white',
              textDecorationLine: 'underline',
              textAlign,
            }}
          >
            {t('try_the_app_before_signing_up')}
          </Label>
        </Pressable>
      </View>
    );
  };

  const renderTermsLinks = (style?: any) => (
    <View style={[{ flexDirection: 'row', alignItems: 'baseline', justifyContent: 'center', flexWrap: 'wrap' }, style]}>
      <Pressable onPress={() => Linking.openURL(t('lanla_website_terms_of_use_url'))}>
        <Label variant={LabelType.titleWhiteSmall} style={{ textDecorationLine: 'underline' }}>
          {t('Terms-and-Conditions')}
        </Label>
      </Pressable>
      <Label style={{ marginLeft: 8, marginRight: 8 }} variant={LabelType.titleWhiteSmall}>
        {t('and')}
      </Label>
      <Pressable onPress={() => Linking.openURL(t('lanla_website_privacy_policy_url'))}>
        <Label variant={LabelType.titleWhiteSmall} style={{ textDecorationLine: 'underline' }}>
          {t('footer.privacy_policy')}
        </Label>
      </Pressable>
    </View>
  );

  const renderReferralInfoLine = (actionKey: string, bodyKey: string) => (
    <Label numberOfLines={4} style={{ color: 'white', fontSize: 16, lineHeight: 22, marginBottom: 10 }}>
      <Label style={{ color: theme.primary, fontSize: 16, lineHeight: 22, fontWeight: '700' }}>{t(actionKey)}</Label>
      {t(bodyKey)}
    </Label>
  );

  const renderReferralMissionPanel = () => (
    <View
      style={{
        width: isDesktopLoginLayout ? 532 : '100%',
        maxWidth: isDesktopLoginLayout ? undefined : 420,
        minHeight: isDesktopLoginLayout ? 470 : undefined,
        marginTop: isDesktopLoginLayout ? 68 : 32,
        padding: isDesktopLoginLayout ? 24 : 20,
        borderRadius: 8,
        borderWidth: 2,
        borderColor: theme.secondaryLight,
        overflow: isDesktopLoginLayout ? 'visible' : 'hidden',
      }}
    >
      <View style={{ maxWidth: isDesktopLoginLayout ? 220 : '100%', position: 'relative', zIndex: 2 }}>
        <Label
          numberOfLines={3}
          variant={LabelType.titleLargeNoCaps}
          style={{ color: 'white', fontSize: isDesktopLoginLayout ? 26 : 24, lineHeight: isDesktopLoginLayout ? 31 : 29, marginBottom: 24 }}
        >
          {t('referral-login-mission-title')}
        </Label>
        {renderReferralInfoLine('referral-login-consult-action', 'referral-login-consult-body')}
        {renderReferralInfoLine('referral-login-complete-action', 'referral-login-complete-body')}
        {renderReferralInfoLine('referral-login-withdraw-action', 'referral-login-withdraw-body')}
        <Label numberOfLines={3} style={{ color: 'white', fontSize: 16, lineHeight: 22, fontWeight: '700', marginTop: 2 }}>
          {t('referral-login-more-features')}
        </Label>
      </View>
      <Image
        resizeMode="contain"
        style={
          isDesktopLoginLayout
            ? { position: 'absolute', width: 420, height: 480, top: -18, right: -150, zIndex: 1 }
            : { width: '100%', maxWidth: 320, aspectRatio: 788 / 900, marginTop: 20, alignSelf: 'center' }
        }
        source={require('../../assets/referral-login.png')}
      />
    </View>
  );

  const renderReferralLayout = () => (
    <ScrollView
      style={{ height: '100%', width: '100%', flexShrink: 0 }}
      contentContainerStyle={{ minHeight: '100%', paddingBottom: 40 }}
      keyboardShouldPersistTaps={'handled'}
    >
      <View
        style={{
          minHeight: '100%',
          width: '100%',
          padding: 16,
          marginTop: 44,
          alignItems: 'center',
          justifyContent: 'flex-start',
        }}
      >
        <View
          style={{
            width: '100%',
            maxWidth: isDesktopLoginLayout ? 1080 : 420,
            flexDirection: isDesktopLoginLayout ? 'row' : 'column',
            justifyContent: 'center',
            alignItems: isDesktopLoginLayout ? 'flex-start' : 'center',
          }}
        >
          <View
            style={{
              width: '100%',
              maxWidth: 312,
              marginRight: isDesktopLoginLayout ? 128 : 0,
              paddingTop: 0,
              flexGrow: 1,
            }}
          >
            <View
              style={{
                flexDirection: 'row',
                alignItems: 'flex-start',
                justifyContent: isDesktopLoginLayout ? 'space-between' : 'flex-end',
                marginBottom: isDesktopLoginLayout ? 48 : 32,
              }}
            >
              {isDesktopLoginLayout && renderLoginLogo(85, 40)}
              {getOtherLanguageButton()}
            </View>
            {renderUserMessage()}
            {renderAuthControls({ width: '100%' })}
            {renderTryAppLink(isDesktopLoginLayout ? 'left' : 'center')}
            {renderTermsLinks({ paddingTop: 32 })}
            {!isDesktopLoginLayout && <View style={{ alignItems: 'center', justifyContent: 'center' }}>{renderLoginLogo(134, 63, { marginTop: 77 })}</View>}
          </View>
          {windowDimensions.width >= 1366 && renderReferralMissionPanel()}
        </View>
      </View>
    </ScrollView>
  );

  return (
    <View style={styles.root}>
      {/* This screen is rendered inside the app SafeAreaView. Extend the background into the inset areas. */}
      <LinearGradient
        pointerEvents="none"
        end={{ x: 0.1, y: 0.4 }}
        style={[StyleSheet.absoluteFillObject, { top: -insets.top, bottom: -insets.bottom }]}
        colors={['#022d38', '#034050', '#044c5e', '#04647c', '#43a49e']}
      />
      <View id={'2fa-recaptcha-container'}>{/* recaptcha used for account verification */}</View>
      {loading && <Loading textVariant={LabelType.loginLoading} iconColor={'white'} />}
      {pageState !== LoginPageState.ShowTermsAndConditions && !loading && isReferralLogin && renderReferralLayout()}
      {pageState !== LoginPageState.ShowTermsAndConditions && !loading && !isReferralLogin && (
        <>
          <ScrollView style={{ height: '100%', width: '100%', flexShrink: 0 }} keyboardShouldPersistTaps={'handled'}>
            <View
              style={{
                height: '100%',
                width: '100%',
                padding: 16,
                marginTop: 44,
                alignItems: 'center',
                flexDirection: 'row',
                justifyContent: 'center',
              }}
            >
              <View
                style={{
                  height: '100%',
                  maxWidth: 312,
                  marginRight: windowDimensions.width >= 1366 ? 128 : 0,
                  marginLeft: windowDimensions.width >= 1366 ? -64 : 0,
                  paddingTop: 68,
                  flexGrow: 1,
                }}
              >
                {route.name === 'login' && (
                  <View
                    style={{
                      width: '100%',
                      flexDirection: 'row',
                      justifyContent: 'flex-end',
                      zIndex: 1000,
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      paddingVertical: 16,
                      // paddingHorizontal: 16,
                    }}
                  >
                    {getOtherLanguageButton()}
                  </View>
                )}
                {windowDimensions.width >= 1366 && (
                  <>
                    {Platform.OS !== 'web' ? (
                      <View style={{ position: 'absolute', top: 0, left: 0 }}>
                        <Logo width={85} height={40} />
                      </View>
                    ) : (
                      <Image style={{ position: 'absolute', top: 0, left: 0, width: 85, height: 40 }} source={require('../../assets/login-page-logo.svg')} />
                    )}
                  </>
                )}
                {userMessage && (userMessage?.length ?? 0) > 0 && (
                  <Label
                    numberOfLines={5}
                    variant={LabelType.titleSmall}
                    style={{
                      padding: 16,
                      marginBottom: 24,
                      backgroundColor: userMessage === invalidCredentialsMessage ? (theme.colorErrorPrimary?.toString() ?? '#a61e49') : theme.primary,
                      color: 'white',
                      ...StylesHelper.borderRadius(8),
                    }}
                  >
                    {userMessage}
                  </Label>
                )}
                <View>
                  {loginComponent}
                  <CustomButton title={t('continue')} onPress={handleContinue} />
                  {returnButtonComponent}
                </View>
                {pageState === LoginPageState.Login && (signUpSource == '' || signUpSource == undefined) && (
                  <View>
                    <Pressable style={{ marginTop: 24 }} onPress={loginAnonymously}>
                      <Label
                        style={{
                          fontSize: 14,
                          color: 'white',
                          textDecorationLine: 'underline',
                          textAlign: windowDimensions.width >= 1366 ? 'left' : 'center',
                        }}
                      >
                        {t('try_the_app_before_signing_up')}
                      </Label>
                    </Pressable>
                  </View>
                )}
                <View style={{ flexDirection: 'row', alignItems: 'baseline', paddingTop: 32, justifyContent: 'center' }}>
                  <Pressable onPress={() => Linking.openURL(t('lanla_website_terms_of_use_url'))}>
                    <Label variant={LabelType.titleWhiteSmall} style={{ textDecorationLine: 'underline' }}>
                      {t('Terms-and-Conditions')}
                    </Label>
                  </Pressable>
                  <Label style={{ marginLeft: 8, marginRight: 8 }} variant={LabelType.titleWhiteSmall}>
                    {t('and')}
                  </Label>
                  <Pressable onPress={() => Linking.openURL(t('lanla_website_privacy_policy_url'))}>
                    <Label variant={LabelType.titleWhiteSmall} style={{ textDecorationLine: 'underline' }}>
                      {t('footer.privacy_policy')}
                    </Label>
                  </Pressable>
                </View>
                {windowDimensions.width < 1366 && (
                  <View style={{ alignItems: 'center', justifyContent: 'center' }}>
                    {Platform.OS !== 'web' ? (
                      <View style={{ marginTop: 77 }}>
                        <Logo width={134} height={63} />
                      </View>
                    ) : (
                      <Image style={{ width: 134, height: 63, marginTop: 77 }} source={require('../../assets/login-page-logo.svg')} />
                    )}
                  </View>
                )}
              </View>
              {windowDimensions.width >= 1366 && (
                <View style={{ height: '100%', paddingTop: 68, flexGrow: 0 }}>
                  <View
                    style={{
                      width: 532,
                      minHeight: 500,
                      ...StylesHelper.borderRadius(5),
                      ...StylesHelper.borderWidth(2),
                      ...StylesHelper.padding(24),
                      borderColor: theme.secondaryLight,
                    }}
                  >
                    <Label numberOfLines={3} variant={LabelType.titleLargeNoCaps} style={{ color: 'white', fontSize: 26, maxWidth: 210, marginBottom: 24 }}>
                      {t('Discover-the-new-Lanla')}
                    </Label>
                    {getDesktopInfo(t('receive'), t('receive-notification-desc'))}
                    {getDesktopInfo(t('cash-out'), t('cash-out-desc'))}
                    {getDesktopInfo(t('login-complete'), t('login-complete-desc'))}
                    <Label style={{ color: 'white', fontSize: 16, fontWeight: '600', marginBottom: 24 }}>{t('several-features')}</Label>
                  </View>
                  <Image
                    style={{ position: 'absolute', width: 438, height: 518, top: 0, right: -148 }}
                    source={require('../../assets/image-home-woman-pin.png')}
                  ></Image>
                </View>
              )}
            </View>
          </ScrollView>
        </>
      )}
      {pageState === LoginPageState.ShowTermsAndConditions && !loading && (
        <ScrollView style={{ height: '100%', width: '100%', flexShrink: 0 }}>
          <View
            style={{
              width: '100%',
              paddingHorizontal: Platform.OS === 'web' ? 16 : 0,
              paddingVertical: Platform.OS === 'web' ? 16 : 0,
              marginTop: 44,
              alignItems: 'center',
              flexDirection: 'column',
              alignContent: 'center',
            }}
          >
            <View
              style={{
                maxWidth: windowDimensions.width >= 1366 ? 1000 : '100%',
                paddingTop: windowDimensions.width >= 1366 ? 68 : 0,
                flexGrow: 1,
                paddingHorizontal: Platform.OS === 'web' ? 12 : 24,
                paddingVertical: Platform.OS === 'web' ? 12 : 24,
                flexDirection: 'row',
              }}
            >
              <View
                style={{
                  width: '100%',
                  flexDirection: 'row',
                  justifyContent: Platform.OS === 'web' ? 'flex-end' : 'flex-start',
                  zIndex: 1000,
                  position: 'absolute',
                  top: Platform.OS === 'web' ? 0 : -28,
                  left: Platform.OS === 'web' ? 0 : 97,
                  paddingVertical: 16,
                  paddingHorizontal: 16,
                }}
              >
                {getOtherLanguageButton()}
              </View>
              {Platform.OS !== 'web' ? (
                <View style={{ position: 'absolute', top: windowDimensions.width >= 1366 ? 0 : -28, left: 16 }}>
                  <Logo width={85} height={40} />
                </View>
              ) : (
                <Image
                  style={{ position: 'absolute', top: windowDimensions.width >= 1366 ? 0 : -40, left: 24, width: 85, height: 40 }}
                  source={require('../../assets/login-page-logo.svg')}
                />
              )}
              <View
                style={{
                  width: '100%',
                  borderWidth: 1,
                  borderRadius: 8,
                  borderStyle: 'solid',
                  // marginTop: windowDimensions.width >= 1366 ? 0 : 40,
                  marginTop: 40,
                  marginBottom: 40,
                  marginLeft: -8,
                  paddingVertical: Platform.OS === 'web' ? 32 : 12,
                  paddingHorizontal: Platform.OS === 'web' ? 32 : 12,
                  paddingRight: Platform.OS === 'web' && windowDimensions.width >= 1366 ? 170 : Platform.OS === 'web' ? 32 : 12,
                  borderColor: 'white',
                  flexDirection: 'column',
                }}
              >
                <Image
                  style={{
                    position: 'absolute',
                    top: -50,
                    right: -40,
                    width: Platform.OS === 'web' && windowDimensions.width >= 1366 ? 204 : 138,
                    height: Platform.OS === 'web' && windowDimensions.width >= 1366 ? 241 : 163,
                  }}
                  source={require('../../assets/SingUpTerms.png')}
                />
                <View style={{ maxWidth: Platform.OS === 'web' ? '80%' : '70%' }}>
                  <Label style={{ ...StylesHelper.text(30, theme.primary, 600) }}>{tLogin('terms-conditions-title')}</Label>
                </View>
                <View style={{ marginBottom: 16, maxWidth: Platform.OS === 'web' ? '80%' : '100%' }}>
                  <Label numberOfLines={1} style={{ ...StylesHelper.text(30, 'white', 600), marginTop: windowDimensions.width >= 1366 ? -10 : 0 }}>
                    {t('mystery-shopper').toLowerCase()}
                  </Label>
                </View>
                <CustomMarkdown
                  style={{
                    body: { color: 'white', fontSize: 14, paddingBottom: 16, flexGrow: 1 },
                    list_item: { marginBottom: 12 },
                  }}
                >
                  {tLogin('terms-conditions')}
                </CustomMarkdown>
                <View style={{ flexDirection: 'row', alignItems: 'baseline', flexShrink: 1, flexWrap: 'wrap' }}>
                  <Label variant={LabelType.titleWhiteSmall}>{tLogin('terms-conditions-plus')}</Label>
                  <Pressable onPress={() => Linking.openURL(t('lanla_website_terms_of_use_url'))}>
                    <Label variant={LabelType.titleWhiteSmall} style={{ textDecorationLine: 'underline' }}>
                      {t('Terms-and-Conditions')}
                    </Label>
                  </Pressable>
                  <Label style={{ marginLeft: 8, marginRight: 8 }} variant={LabelType.titleWhiteSmall}>
                    {t('and')}
                  </Label>
                  <Pressable onPress={() => Linking.openURL(t('lanla_website_privacy_policy_url'))}>
                    <Label variant={LabelType.titleWhiteSmall} style={{ textDecorationLine: 'underline' }}>
                      {t('footer.privacy_policy')}
                    </Label>
                  </Pressable>
                </View>
                <CustomButton
                  style={{ maxWidth: windowDimensions.width >= 1366 ? '40%' : '100%', textAlign: 'center', marginTop: 24 }}
                  title={tLogin('terms-conditions-button')}
                  onPress={() => setPageState(LoginPageState.Register)}
                />
              </View>
            </View>
            {(windowDimensions.width < 1366 || Platform.OS !== 'web') && (
              <View style={{ alignItems: 'center', justifyContent: 'center' }}>
                {Platform.OS !== 'web' ? (
                  <View style={{ marginTop: 32, marginBottom: 24 }}>
                    <Logo width={134} height={63} />
                  </View>
                ) : (
                  <Image style={{ width: 134, height: 63, marginTop: 32, marginBottom: 24 }} source={require('../../assets/login-page-logo.svg')} />
                )}
              </View>
            )}
          </View>
        </ScrollView>
      )}
    </View>
  );
};

export default Login;

const styles = StyleSheet.create({
  root: {
    flex: 1,
  },
});
